import {redirect} from 'next/navigation';

import {resolveRoute} from '@/lib/routes';
import {shouldThrowError} from '@/lib/utils';
import {getUserData, isAuthenticated} from '@/lib/helper';
import {getSettingsItemsBlock} from '@/lib/fetch/settings';
import {getKYCGift} from '@/lib/fetch/customer-loyalty-program';
import type {Profile} from '@/types/api/profile';
import type {UserVerificationStatus} from '@/types/api/users';
import type {KYCGift} from '@/types/api/customer-loyalty-program';

export const verificationStatuses = [
  ['CREATED', 'CONTACT_VERIFIED'],
  ['IDENTITY_VERIFIED', 'ID_CARD_REJECTED'],
  ['ID_CARD_VERIFIED', 'SELFI_REJECTED'],
  ['PROCESSING', 'REJECTED', 'VERIFIED']
];

/** Whether the document verification is in rejection state. */
export function isIdCardRejected(status: UserVerificationStatus) {
  return status === 'ID_CARD_REJECTED';
}

/** Whether the visual verification is in rejection state. */
export function isSelfieRejected(status: UserVerificationStatus) {
  return status === 'SELFI_REJECTED';
}

/**
 * Whether the document verification is in rejection state
 * while the visual verification has been verified before.
 */
export function isIdCardRejectedAndSelfieVerified(
  status: UserVerificationStatus,
  profile: Profile
) {
  return isIdCardRejected(status) && !!profile.selfi;
}

/** Whether the identity verification is in processing state. */
export function isProcessing(status: UserVerificationStatus) {
  return status === 'PROCESSING';
}

/**
 * Whether the identity verification is in rejection state.
 * @param completely - Is identity verification is completely rejected.
 */
export function isRejected(
  status: UserVerificationStatus,
  completely = true
): boolean {
  return completely
    ? status === 'REJECTED'
    : isIdCardRejected(status) ||
        isSelfieRejected(status) ||
        isRejected(status, true);
}

/**
 * Whether the identity verification is in verified state.
 * @param completely - Is identity verification is completely verified.
 */
export function isVerified(status: UserVerificationStatus, completely = true) {
  return completely
    ? status === 'VERIFIED'
    : isDocumentStep(status) && !isRejected(status);
}

/** Whether the current step is related to document verification. */
export function isDocumentStep(status: UserVerificationStatus) {
  return !verificationStatuses[0].includes(status);
}

/** Whether the current step is related to visual verification. */
export function isRecordingStep(status: UserVerificationStatus) {
  return ![...verificationStatuses[0], ...verificationStatuses[1]].includes(
    status
  );
}

/** Whether the current step is related to verification result. */
export function isResultStep(status: UserVerificationStatus) {
  return ![
    ...verificationStatuses[0],
    ...verificationStatuses[1],
    ...verificationStatuses[2]
  ].includes(status);
}

/**
 * Return the due date that the user is able
 * to acquire the identity verification gift.
 */
export function getKYCGiftExpirationDate(kycGift: KYCGift) {
  const expirationDate = new Date(kycGift.createdAt);
  expirationDate.setDate(expirationDate.getDate() + kycGift.expirationDays);

  return expirationDate;
}

/** Whether the user still can acquire the identity verification gift. */
export function isKYCGiftAcquirable(kycGift: KYCGift) {
  return !kycGift.acquired && new Date() < getKYCGiftExpirationDate(kycGift);
}

/** Whether the given route is accessible based on the user's verification status. */
export function isRouteAccessible(
  route: string,
  status: UserVerificationStatus
) {
  if (
    route === resolveRoute('base.identity-verification') ||
    (route !== resolveRoute('base.identity-verification.result') &&
      isRejected(status))
  ) {
    return false;
  } else if (verificationStatuses[3].includes(status)) {
    return true;
  }

  switch (route) {
    case resolveRoute('base.identity-verification.gift'):
    case resolveRoute('base.identity-verification.basic'):
      return true;
    case resolveRoute('base.identity-verification.document'):
      return isDocumentStep(status);
    case resolveRoute('base.identity-verification.recording'):
      return isRecordingStep(status);
    case resolveRoute('base.identity-verification.result'):
      return isResultStep(status);
    default:
      return false;
  }
}

/**
 * Redirects from the specified route to the appropriate verification step
 * based on user's verification status, ensuring the verification process
 * is continued from the correct point.
 */
export async function checkRoute(route: string) {
  const userData = await getUserData(true);

  /**
   * @bug Because of an unknown reason, if this placed in `layout.tsx`
   * file, the gift page would not be accessible due to redirection
   * to the login page no matter the user is logged in or not.
   * This only happens after user is already redirected from the
   * registration page to the gift page or from the login page
   * to the homepage and get redirected to the login page again by
   * clicking on the gift page links. This also only happens in the
   * production environment.
   *
   * @bug Due to using the `loading.tsx` file, the redirection will
   * be handled on the client side and hence the page flashes before redirection.
   */
  if (!(await isAuthenticated(userData)))
    redirect(
      resolveRoute(
        route === resolveRoute('base.identity-verification.gift')
          ? 'base.register'
          : 'base.login'
      )
    );

  let redirectToGift = false;
  const status = userData?.profile?.status;
  const isRouteNotAccessible = status && !isRouteAccessible(route, status);
  try {
    if (status === 'CREATED' && isRouteNotAccessible) {
      const [{kycGift: isKYCGiftDisabled}, kycGift] = await Promise.all([
        getSettingsItemsBlock(),
        getKYCGift()
      ]);

      if (!isKYCGiftDisabled && !kycGift) {
        redirectToGift = true;
      }
    }
  } catch (error) {
    shouldThrowError(error);
  }

  if (isRouteNotAccessible)
    if (redirectToGift)
      redirect(resolveRoute('base.identity-verification.gift'));
    else if (!isDocumentStep(status))
      redirect(resolveRoute('base.identity-verification.basic'));
    else if (verificationStatuses[1].includes(status))
      redirect(resolveRoute('base.identity-verification.document'));
    else if (verificationStatuses[2].includes(status))
      redirect(resolveRoute('base.identity-verification.recording'));
    else if (verificationStatuses[3].includes(status))
      redirect(resolveRoute('base.identity-verification.result'));
}
